import { useEventListener } from '@react-hookz/web';
import { useNavigate } from '@tanstack/react-router';
import Cookies from 'js-cookie';
import { useCallback, useEffect, useRef } from 'react';
import { create } from 'zustand';

import { GebruikerSchema } from '@/api';
import { Allowed } from '@/components/RoleBasedAccess';
import API from '@/services/axios';
import { queryClient } from '@/services/queryClient';
import { environment } from '@/utils/environment';

interface UserState {
  user: GebruikerSchema | null;
  userRole: Allowed[] | null;
  area: 'landelijk' | 'omgevingsdienst' | null;
  organisatieNaam: string | null;
  userIsLoading: boolean;
  isInitialised: boolean;
  isLoggedIn: boolean;
  method: SSOMethod | OldMethod | null;
}

type SSOMethod = {
  type: 'sso';
  /** The tenant cookie is the tenant of the user */
  tenantCookie: string;
  /** The session coookie is the timestamp when the session expires */
  sessionCookie: number;
};

type OldMethod = {
  type: 'old';
};

interface UserActions {
  loadLoggedInUser: () => Promise<void>;
  logout: (onSuccess?: () => void) => Promise<void>;
  loginOld: ({
    email,
    password,
  }: {
    email: string;
    password: string;
  }) => Promise<void>;
  refreshSSOSession: () => Promise<void>;
}

const initialState: UserState = {
  user: null,
  userRole: null,
  area: null,
  organisatieNaam: null,
  userIsLoading: false,
  isLoggedIn: false,
  isInitialised: false,
  method: null,
};

const parseRol = (rol: string) => rol.split(',') as Allowed[];

export const useUserStore = create<UserState & UserActions>()((set, get) => ({
  ...initialState,
  loadLoggedInUser: async () => {
    const {
      /** The access token is only set when a user is logged in via the old method */
      localStorageToken,
      /** These cookies are only set when a user is logged in via SSO */
      sessionCookie,
      tenantCookie,
    } = getAccessToken();

    const hasAccessToken = !!localStorageToken || !!sessionCookie;

    if (!hasAccessToken) {
      set(() => ({
        user: null,
        isLoggedIn: false,
        userRole: ['non-auth'],
        userIsLoading: false,
        isInitialised: true,
        area: null,
        organisatieNaam: null,
        method: null,
      }));

      return;
    }

    const method: SSOMethod | OldMethod =
      sessionCookie && tenantCookie
        ? {
            type: 'sso',
            tenantCookie,
            sessionCookie: parseInt(sessionCookie),
          }
        : {
            type: 'old',
          };

    set(() => ({ userIsLoading: true, method }));

    try {
      const response = await API.get('/auth/tokeninfo');

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const user = response.data.identifier as any;

      set(() => ({
        user,
        userIsLoading: false,
        userRole: user.Rol ? parseRol(user.Rol) : ['non-auth'],
        area: user.Landelijk ? 'landelijk' : 'omgevingsdienst',
        organisatieNaam: user.Organisatienaam,
        isLoggedIn: true,
        isInitialised: true,
      }));
    } catch {
      set(() => ({
        user: null,
        userIsLoading: false,
        userRole: ['non-auth'],
        isInitialised: true,
        area: null,
        organisatieNaam: null,
        method: null,
      }));
    }
  },
  loginOld: async ({ email, password }) => {
    set(() => ({ userIsLoading: true, method: { type: 'old' } }));

    try {
      const {
        data: { access_token, identifier, expires },
      } = await API.post('/auth/login', {
        identifier: email,
        password,
      });

      localStorage.setItem('__AADV_identifier__', JSON.stringify(identifier));
      localStorage.setItem('__AADV_access_token__', access_token);
      localStorage.setItem('__AADV_access_token_expires__', expires);

      set(() => ({
        user: identifier,
        isLoggedIn: true,
        userIsLoading: false,
        userRole: identifier.Rol ? parseRol(identifier.Rol) : ['non-auth'],
        area: identifier.Landelijk ? 'landelijk' : 'omgevingsdienst',
        organisatieNaam: identifier.Organisatienaam,
      }));
    } catch (error) {
      set(() => ({
        user: null,
        userIsLoading: false,
        userRole: null,
        isLoggedIn: false,
        area: null,
        organisatieNaam: null,
        method: null,
      }));

      throw error;
    }
  },
  logout: async (onSuccess?: () => void) => {
    /**
     * When logged in via SSO, we need to call the logout endpoint of the auth api
     */
    const method = useUserStore.getState().method;

    if (method?.type === 'sso') {
      await API.post(environment.getLogoutURL());
    }

    /**
     * When logged in via the old method, we need to remove the local
     * storage items manually
     */
    localStorage.removeItem('__AADV_identifier__');
    localStorage.removeItem('__AADV_access_token__');
    localStorage.removeItem('__AADV_access_token_expires__');

    /**
     * Make sure a user doesn't see any stale data from when logged in.
     */
    await queryClient.resetQueries();

    set(() => ({
      user: null,
      userIsLoading: false,
      userRole: null,
      isLoggedIn: false,
      area: null,
      organisatieNaam: null,
      method: null,
    }));

    onSuccess?.();
  },
  refreshSSOSession: async () => {
    const method = get().method;

    /**
     * When logged in via the old method, we don't need to refresh the session
     */
    if (method?.type !== 'sso') {
      throw new Error('User is not logged in via SSO');
    }

    try {
      await API.post(environment.getRefreshURL(method.tenantCookie));

      const sessionCookie = Cookies.get('__session');

      if (!sessionCookie) {
        get().logout();

        throw new Error('No session cookie found');
      }

      set(() => ({
        method: {
          type: 'sso',
          tenantCookie: method.tenantCookie,
          sessionCookie: parseInt(sessionCookie),
        },
      }));
    } catch {
      get().logout();

      throw new Error('Failed to refresh SSO session');
    }
  },
}));

export const useRefreshSession = () => {
  const hasInteractedTheLast60Minutes = useRef(false);
  const interactionTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const refreshTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const refreshSSOSession = useUserStore((user) => user.refreshSSOSession);
  const logout = useUserStore((user) => user.logout);
  const sessionCookie = useUserStore((user) => {
    return user.method?.type === 'sso' ? user.method.sessionCookie : null;
  });
  const tenantCookie = useUserStore((user) => {
    return user.method?.type === 'sso' ? user.method.tenantCookie : null;
  });

  const navigate = useNavigate();

  /**
   * Function to refresh the session
   */
  const refresh = useCallback(async () => {
    try {
      await refreshSSOSession();
    } catch {
      navigate({
        to: '/',
        search: {
          p: 1,
          error: 'uitgelogd',
        },
      });
    }
  }, [navigate, refreshSSOSession]);

  /**
   * Track if a user has interacted with the page in the last 60 minutes
   */
  const interaction = useCallback(() => {
    hasInteractedTheLast60Minutes.current = true;

    if (interactionTimeoutRef.current) {
      clearTimeout(interactionTimeoutRef.current);
    }

    interactionTimeoutRef.current = setTimeout(
      () => {
        hasInteractedTheLast60Minutes.current = false;
      },
      1000 * 60 * 60
    );
  }, []);

  useEventListener(document, 'click', interaction);
  useEventListener(document, 'keydown', interaction);

  /**
   * Refresh the session 5 minutes before it expires
   */
  useEffect(() => {
    let isMounted = true;

    if (refreshTimeoutRef.current) {
      clearTimeout(refreshTimeoutRef.current);
    }

    // The __tenant and __session cookies will only be set when a user is logged in via SSO
    if (tenantCookie && sessionCookie) {
      const expireTimestamp = sessionCookie;
      const expireDate = new Date(expireTimestamp);
      const now = new Date();

      const msTillExpire = expireDate.getTime() - now.getTime();

      if (msTillExpire > 0) {
        const msToRefresh = Math.max(msTillExpire - 1000 * 60 * 5, 0);

        refreshTimeoutRef.current = setTimeout(() => {
          if (!isMounted) return;

          if (hasInteractedTheLast60Minutes.current) {
            refresh();
          } else {
            logout(() => {
              navigate({
                to: '/',
                search: {
                  p: 1,
                  error: 'uitgelogd',
                },
              });
            });
          }
        }, msToRefresh);
      }
    }

    return () => {
      isMounted = false;
    };
  }, [refresh, navigate, logout, tenantCookie, sessionCookie]);

  /**
   * Cleanup the timeouts when the component unmounts
   */
  useEffect(() => {
    return () => {
      if (interactionTimeoutRef.current) {
        clearTimeout(interactionTimeoutRef.current);
      }

      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);
};

const getAccessToken = () => {
  /** The access token is only set when a user is logged in via the old method */
  const localStorageToken = localStorage.getItem('__AADV_access_token__');

  /** These cookies are only set when a user is logged in via SSO */
  const sessionCookie = Cookies.get('__session');
  const tenantCookie = Cookies.get('__tenant');

  return {
    localStorageToken,
    sessionCookie,
    tenantCookie,
  };
};

export const hasAccessToken = () => {
  const { localStorageToken, sessionCookie } = getAccessToken();

  return !!localStorageToken || !!sessionCookie;
};
